import React from 'react';
import { Box, Typography, Paper, Button, Chip, Grid } from '@leap4/frontend';
import { MuiIcons } from '@leap4/frontend';
import { IReportTemplate } from '../report.interfaces';
import { REPORT_METRICS, REPORT_DIMENSIONS, AGGREGATION_OPTIONS } from '../report.constants';

const { Bookmark, PlayArrow, Delete } = MuiIcons;

interface TemplatesTabProps {
  templates: IReportTemplate[];
  onLoadTemplate: (template: IReportTemplate) => void;
  onDeleteTemplate: (id: string) => void;
}

export const TemplatesTab: React.FC<TemplatesTabProps> = ({
  templates,
  onLoadTemplate,
  onDeleteTemplate,
}) => {
  const getMetricLabel = (key: string) => REPORT_METRICS.find((m) => m.key === key)?.label || key;
  const getDimensionLabel = (key: string) => REPORT_DIMENSIONS.find((d) => d.key === key)?.label || key;
  const getAggregationLabel = (key: string) => AGGREGATION_OPTIONS.find((a) => a.key === key)?.label || key;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600} color="text.primary">
          Saved Templates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {templates.length} template{templates.length !== 1 ? 's' : ''} available
        </Typography>
      </Box>

      {templates.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Bookmark sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No templates saved yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            Create a report configuration and save it as a template for quick access
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {templates.map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.15s',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    borderColor: 'primary.light',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: 'primary.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.main',
                    }}
                  >
                    <Bookmark />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {template.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {template.createdAt}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  {template.config.metrics.slice(0, 4).map((m) => (
                    <Chip
                      key={m}
                      label={getMetricLabel(m)}
                      size="small"
                      color="primary"
                      sx={{ fontSize: 11, height: 22 }}
                    />
                  ))}
                  {template.config.metrics.length > 4 && (
                    <Chip
                      label={`+${template.config.metrics.length - 4}`}
                      size="small"
                      sx={{ fontSize: 11, height: 22, bgcolor: 'grey.100' }}
                    />
                  )}
                  {template.config.dimensions.slice(0, 2).map((d) => (
                    <Chip
                      key={d}
                      label={getDimensionLabel(d)}
                      size="small"
                      sx={{ fontSize: 11, height: 22, bgcolor: 'grey.100' }}
                    />
                  ))}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  {getAggregationLabel(template.config.aggregationPeriod)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pt: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PlayArrow />}
                    onClick={() => onLoadTemplate(template)}
                    sx={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #1565c0, #1976d2)',
                    }}
                  >
                    Load & Run
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onDeleteTemplate(template.id)}
                    sx={{
                      minWidth: 40,
                      px: 1,
                      color: 'text.disabled',
                      borderColor: 'divider',
                      '&:hover': { color: 'error.main', borderColor: 'error.main' },
                    }}
                  >
                    <Delete fontSize="small" />
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
